import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Logo from "./Logo";
import NotificationsBell from "./NotificationsBell";
import {
  House, UsersThree, User, SignOut, ChartLineUp, Receipt, List, X, Brain,
} from "@phosphor-icons/react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  const NavItem = ({ to, icon: Icon, label, testid }) => (
    <NavLink
      to={to}
      data-testid={testid}
      onClick={closeMobile}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest font-semibold transition-colors border-l-2 ${
          isActive
            ? "text-white border-[#FF3B30] bg-[#1F1F1F]"
            : "text-[#A3A3A3] border-transparent hover:text-white hover:bg-[#141414]"
        }`
      }
    >
      <Icon size={20} weight="bold" />
      {label}
    </NavLink>
  );

  const Brand = ({ size = 80, onClick }) => (
    <Link to="/dashboard" className="p-6 border-b border-app block" data-testid="brand-link" onClick={onClick}>
      <div className="flex flex-col items-center gap-3 text-center">
        <Logo size={size} />
        <div>
          <div className="font-display font-black text-2xl tracking-tighter uppercase leading-tight">Athlon Sports</div>
          <div className="text-[10px] text-muted uppercase tracking-widest">Training Control</div>
        </div>
      </div>
    </Link>
  );

  const SidebarBody = (
    <>
      <nav className="flex-1 py-4 flex flex-col">
        <NavItem to="/dashboard" icon={House} label="Dashboard" testid="nav-dashboard" />
        {(user?.role === "admin" || user?.role === "profesor") && (
          <NavItem to="/users" icon={UsersThree} label={user?.role === "profesor" ? "Mis atletas" : "Usuarios"} testid="nav-users" />
        )}
        <NavItem to={`/profile/${user?.user_id}`} icon={User} label="Mi Perfil" testid="nav-profile" />
        {user?.role !== "profesor" && (
          <NavItem to="/payments" icon={Receipt} label="Pagos" testid="nav-payments" />
        )}
        <NavItem to="/neurociencia" icon={Brain} label="Neurociencia" testid="nav-neuroscience" />
        {(user?.role === "admin" || user?.role === "profesor") && (
          <NavItem to="/exercises" icon={ChartLineUp} label="Ejercicios" testid="nav-exercises" />
        )}
      </nav>

      <div className="p-4 border-t border-app">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center text-white font-bold font-display text-lg overflow-hidden">
            {user?.profile?.avatar_url ? (
              <img src={user.profile.avatar_url} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              user?.name?.[0]?.toUpperCase() || "?"
            )}
          </div>
          <div className="overflow-hidden">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-muted uppercase tracking-widest">{user?.role}</div>
          </div>
        </div>
        <button onClick={() => { closeMobile(); handleLogout(); }} className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="logout-btn">
          <SignOut size={16} weight="bold" />
          Cerrar Sesión
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-app flex">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-surface border-r border-app hidden md:flex flex-col" data-testid="sidebar">
        <Brand />
        {SidebarBody}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/70"
          onClick={closeMobile}
          data-testid="mobile-nav-backdrop"
        />
      )}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-surface border-r border-app flex flex-col transform transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-sidebar"
      >
        <div className="flex items-center justify-end p-2 border-b border-app">
          <button
            onClick={closeMobile}
            className="text-muted hover:text-white p-2"
            data-testid="close-mobile-nav"
            aria-label="Cerrar menú"
          >
            <X size={22} weight="bold" />
          </button>
        </div>
        <Brand size={64} onClick={closeMobile} />
        {SidebarBody}
      </aside>

      <main className="flex-1 overflow-x-hidden flex flex-col">
        <div className="bg-surface border-b border-app px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-white p-1 -ml-1"
              data-testid="open-mobile-nav"
              aria-label="Abrir menú"
            >
              <List size={26} weight="bold" />
            </button>
            <Link to="/dashboard" className="flex items-center gap-3 md:invisible">
              <Logo size={36} />
              <span className="font-display font-black text-lg uppercase tracking-tighter">Athlon Sports</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <button onClick={handleLogout} className="text-muted p-2 md:hidden" data-testid="logout-btn-mobile">
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
